import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { agendaBlockPayload } from "./AgendaTemplateView";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, ArrowUp, ArrowDown, Download, Mail, Save, Loader2, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { SkillFocusPanel, matchesFocus } from "./SkillFocusPanel";

const BLOCK_TYPES = ["Announcements", "Discussion", "Movement Warm Up", "Brain Warm Up", "Game", "Wrap Up", "If Time", "Home Improv"];
const NEW_BLOCK = () => ({ type: "Game", duration_min: 15, title: "", content: "", game_id: "" });
const inp = "w-full h-10 px-3 rounded-lg border border-[#d6cecf]";

export default function AgendaBuild() {
  const location = useLocation();
  const [params] = useSearchParams();
  const [title, setTitle] = useState("");
  const [meta, setMeta] = useState("");
  const [skill, setSkill] = useState("");
  const [level, setLevel] = useState("");
  const [blocks, setBlocks] = useState([NEW_BLOCK()]);
  const [games, setGames] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const loadDrafts = () => api.get("/agendas/drafts").then(({ data }) => setDrafts(data)).catch(() => {});

  useEffect(() => {
    api.get("/games").then(({ data }) => setGames(data.slice().sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {});
    loadDrafts();
  }, []);

  const loadInto = (t) => {
    setTitle(t.title || "");
    setMeta(t.meta || "");
    setSkill(t.skill_focus || "");
    setLevel(t.level || "");
    setBlocks((t.blocks || []).map((b) => ({
      type: b.type || "Game", duration_min: b.duration_min ?? 0, title: b.title || "",
      content: b.content || "", game_id: b.game_id || (b.game && b.game.id) || "",
    })));
  };

  useEffect(() => {
    if (location.state?.blocks) { loadInto(location.state); return; }
    const draftId = params.get("draft");
    if (draftId) api.get(`/agendas/drafts/${draftId}`).then(({ data }) => loadInto(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setB = (i, k, v) => setBlocks((bs) => bs.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  const addBlock = () => setBlocks((bs) => [...bs, NEW_BLOCK()]);
  const removeBlock = (i) => setBlocks((bs) => bs.filter((_, j) => j !== i));
  const move = (i, d) => setBlocks((bs) => {
    const n = [...bs]; const j = i + d;
    if (j < 0 || j >= n.length) return bs;
    [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const payload = () => ({ title, meta, skill_focus: skill || null, level, blocks: agendaBlockPayload(blocks) });
  const gameBlock = (g) => ({ type: "Game", duration_min: 15, title: g.name, content: "", game_id: g.id });
  const addSuggested = (g) => {
    setBlocks((bs) => [...bs.filter((b) => b.title || b.game_id || b.content), gameBlock(g)]);
    toast.success(`Added "${g.name}"`);
  };
  const autoBuild = () => {
    if (blocks.some((b) => b.title || b.game_id || b.content) && !window.confirm("Replace the current segments with a starter agenda?")) return;
    const pool = games.filter((g) => matchesFocus(g, skill, level)).sort(() => Math.random() - 0.5).slice(0, 3);
    setBlocks([
      { type: "Movement Warm Up", duration_min: 10, title: "Warm up", content: `Get bodies and voices ready to practise ${skill}.`, game_id: "" },
      ...pool.map(gameBlock),
      { type: "Wrap Up", duration_min: 10, title: "Closing", content: `Reflect: when did we use ${skill} today?`, game_id: "" },
    ]);
    if (!title.trim()) setTitle(`Skill Focus: ${skill}`);
    toast.success("Starter agenda built. Edit anything you like.");
  };
  const usedIds = new Set(blocks.map((b) => b.game_id).filter(Boolean));
  const matchGames = skill ? games.filter((g) => matchesFocus(g, skill, level)) : [];
  const otherGames = skill ? games.filter((g) => !matchesFocus(g, skill, level)) : games;
  const total = blocks.reduce((s, b) => s + (parseInt(b.duration_min, 10) || 0), 0);

  const download = async () => {
    if (!title.trim()) { toast.error("Give your agenda a title first"); return; }
    try {
      const res = await api.post("/agendas/render/pdf", payload(), { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `agenda_${title.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download PDF"); }
  };

  const sendEmail = async () => {
    if (!email.trim()) { toast.error("Enter an email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/agendas/render/email", { ...payload(), email: email.trim() });
      toast.success(`Agenda emailed to ${data.sent_to}`); setEmailOpen(false); setEmail("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveDraft = async () => {
    if (!title.trim()) { toast.error("Give your agenda a title first"); return; }
    try { await api.post("/agendas/drafts", payload()); toast.success("Draft saved"); loadDrafts(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openDraft = async (id) => {
    try { const { data } = await api.get(`/agendas/drafts/${id}`); loadInto(data); setDraftsOpen(false); toast.success("Draft loaded"); }
    catch { toast.error("Could not load draft"); }
  };
  const deleteDraft = async (id) => {
    try { await api.delete(`/agendas/drafts/${id}`); loadDrafts(); } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="agenda-build">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <span className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#920e12]">Build Your Own</span>
          <h1 className="mt-2 font-display text-4xl text-[#550001]">Agenda builder</h1>
          <p className="mt-2 text-[#545454]">Start from scratch or from a library agenda. {total} min total run time.</p>
        </div>
        <Button onClick={() => setDraftsOpen(true)} variant="outline" className="border-[#920e12] text-[#920e12] shrink-0" data-testid="build-drafts-btn">
          <FolderOpen size={16} className="mr-1" /> My drafts ({drafts.length})
        </Button>
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-[#dfd8da] p-6 space-y-4">
        <div>
          <Label htmlFor="a-title">Agenda title</Label>
          <input id="a-title" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="build-title" className={`mt-1 ${inp}`} placeholder="e.g., Session 1 · Class 1 — Yes, And" />
        </div>
        <div>
          <Label htmlFor="a-meta">Subtitle / details (optional)</Label>
          <input id="a-meta" value={meta} onChange={(e) => setMeta(e.target.value)} data-testid="build-meta" className={`mt-1 ${inp}`} placeholder="e.g., Beginner · Tuesdays 6:30pm" />
        </div>
      </div>

      <SkillFocusPanel skill={skill} setSkill={setSkill} level={level} setLevel={setLevel} games={games}
        usedIds={usedIds} onAdd={addSuggested} onAutoBuild={autoBuild} />

      <div className="mt-4 space-y-3" data-testid="build-blocks">
        {blocks.map((b, i) => (
          <div key={i} className="bg-white rounded-2xl border border-[#dfd8da] p-4" data-testid={`build-block-${i}`}>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 shrink-0 rounded-full bg-[#920e12] text-white text-sm flex items-center justify-center font-bold">{i + 1}</span>
              <div className="ml-auto flex gap-1">
                <Button size="sm" variant="outline" onClick={() => move(i, -1)} className="h-8 w-8 p-0 border-[#d6cecf]" data-testid={`build-up-${i}`}><ArrowUp size={14} /></Button>
                <Button size="sm" variant="outline" onClick={() => move(i, 1)} className="h-8 w-8 p-0 border-[#d6cecf]" data-testid={`build-down-${i}`}><ArrowDown size={14} /></Button>
                <Button size="sm" variant="outline" onClick={() => removeBlock(i)} className="h-8 w-8 p-0 border-[#991b1b] text-[#991b1b]" data-testid={`build-remove-${i}`}><Trash2 size={14} /></Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <select value={b.type} onChange={(e) => setB(i, "type", e.target.value)} className={inp} data-testid={`build-type-${i}`}>
                {BLOCK_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <input type="number" min="0" value={b.duration_min} onChange={(e) => setB(i, "duration_min", e.target.value)} className={inp} data-testid={`build-min-${i}`} placeholder="Minutes" />
              <input value={b.title} onChange={(e) => setB(i, "title", e.target.value)} className={`${inp} sm:col-span-2`} data-testid={`build-blocktitle-${i}`} placeholder="Segment title" />
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <select value={b.game_id} onChange={(e) => setB(i, "game_id", e.target.value)} className={inp} data-testid={`build-game-${i}`}>
                <option value="">— No game —</option>
                {skill ? (
                  <>
                    <optgroup label={`Matches ${skill}`}>{matchGames.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</optgroup>
                    <optgroup label="Other games">{otherGames.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</optgroup>
                  </>
                ) : games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <input value={b.content} onChange={(e) => setB(i, "content", e.target.value)} className={inp} data-testid={`build-content-${i}`} placeholder="Notes / description (optional)" />
            </div>
          </div>
        ))}
        <Button onClick={addBlock} variant="outline" className="w-full border-dashed border-[#920e12] text-[#920e12]" data-testid="build-add-block"><Plus size={16} className="mr-1" /> Add segment</Button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <Button onClick={saveDraft} className="bg-[#920e12] hover:bg-[#550001] text-white" data-testid="build-save"><Save size={16} className="mr-1" /> Save draft</Button>
        <Button onClick={download} variant="outline" className="border-[#920e12] text-[#920e12]" data-testid="build-download"><Download size={16} className="mr-1" /> Download PDF</Button>
        <Button onClick={() => setEmailOpen(true)} variant="outline" className="border-[#920e12] text-[#920e12]" data-testid="build-email"><Mail size={16} className="mr-1" /> Email</Button>
      </div>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="build-email-dialog">
          <DialogHeader><DialogTitle>Email this agenda</DialogTitle>
            <DialogDescription>Send to any registered program account.</DialogDescription></DialogHeader>
          <div><Label htmlFor="b-email">Recipient email</Label>
            <input id="b-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`mt-1 ${inp}`} data-testid="build-email-input" placeholder="name@example.com" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={busy} className="bg-[#920e12] text-white" data-testid="build-email-send">
              {busy ? <><Loader2 size={16} className="mr-1 animate-spin" /> Sending…</> : <><Mail size={16} className="mr-1" /> Send</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={draftsOpen} onOpenChange={setDraftsOpen}>
        <DialogContent data-testid="build-drafts-dialog">
          <DialogHeader><DialogTitle>My saved agendas</DialogTitle>
            <DialogDescription>Load or delete an agenda you saved earlier.</DialogDescription></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {drafts.map((d) => (
              <div key={d.id} className="flex items-center gap-2 border border-[#dfd8da] rounded-lg p-3" data-testid={`draft-${d.id}`}>
                <div className="flex-1"><div className="font-bold text-[#1a1617]">{d.title || "Untitled"}</div><div className="text-xs text-[#545454]">{d.block_count} segments{d.skill_focus ? ` · ${d.skill_focus}` : ""}</div></div>
                <Button size="sm" onClick={() => openDraft(d.id)} className="bg-[#920e12] text-white" data-testid={`draft-open-${d.id}`}>Open</Button>
                <Button size="sm" variant="outline" onClick={() => deleteDraft(d.id)} className="border-[#991b1b] text-[#991b1b]" data-testid={`draft-delete-${d.id}`}><Trash2 size={14} /></Button>
              </div>
            ))}
            {drafts.length === 0 && <p className="text-[#545454] text-sm">No saved agendas yet.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
